import React, { useMemo, useState } from "react";
import { Button, Col, Form, Input, InputNumber, Modal, Pagination, Row, Select, Space, Table, Tag, Typography, message } from "antd";
import type { PaginationProps, TableProps } from "antd";
import "./CombustionTable.css";
import { BsPencilSquare, BsTrashFill } from "react-icons/bs";
import { ResponseCompanyMapping } from "src/entity/company/company-mapping.entity";
import { usePagination } from "src/hook/usePagination";
import { useDidMount } from "src/hook/useDidMount";
import { settingStore } from "src/stores/setting-store";
import { useShallow } from "zustand/react/shallow";
import WrapCenter from "src/components/WrapCenter/WrapCenter";
import { PlusCircleOutlined } from "@ant-design/icons";
import { generateErrorMessage } from "src/utils/error-message";
import { Option } from "src/entity/settings/masterdata";
import VocsModal from "src/components/Modal";
import { uniqBy, omitBy, isNull, isNil } from "lodash";
import {
  CreateSettingFlareInfo,
  FilterSettingFlareInfo,
  ResponseSettingFlareInfo,
  UpdateSettingFlareInfo,
  flareInfoUnitOptions,
  flareTypeOptions,
} from "src/entity/settings/flare-info";
import { profileStore } from "src/stores/profile-store";
import { replaceUndefinedWithNull, trimAllKey } from "src/utils/utils-object";
import { CommonSuccess } from "src/entity/common/menus-enum.enum";
import FormLabel from "src/components/AntdCustomPart/FormLabel/FormLabel";
import CustomInputNumber from "src/components/AntdCustomPart/InputNumber/CustomInputNumber";
import ModalTitle from "src/components/AntdCustomPart/ModalTitle/ModalTitle";
import { reportFlareStore } from "src/stores/report-flare-volume-store";
import useNavigationMenu from "src/hook/useNavigationMenu";
import { canEditTable } from "./PermissionCheck/PermissionCheck";

const { Text } = Typography;
interface FlareInformationTableProps {}

const FlareInformationTable: React.FC<FlareInformationTableProps> = ({}) => {
  const [messageApi, contextHolder] = message.useMessage();
  const [formSettingFlareInfo] = Form.useForm();
  const [visibleModal, setVisibleModal] = useState(false);
  const getSettingFlareInfo = settingStore(useShallow((state) => state.getSettingFlareInfo));
  const dataSettingFlareInfo = settingStore(useShallow((state) => state.dataSettingFlareInfo));
  const totalDocs = dataSettingFlareInfo.total;
  const [isEdit, setIsEdit] = useState<UpdateSettingFlareInfo | null>(null);
  const { permissions } = useNavigationMenu();

  const profile = profileStore(useShallow((state) => state.profile));
  const companies = profile?.company ?? [];
  const emissionData = settingStore(useShallow((state) => state.emission));
  const unitData = settingStore(useShallow((state) => state.unit));
  const getFilterColumn = settingStore(useShallow((state) => state.getFilterColumn));
  const filterFlareInfo = settingStore(useShallow((state) => state.filterFlareInfo));
  const loadingManageFlareInfo = settingStore(useShallow((state) => state.loadingManageFlareInfo));
  const createSettingFlareInfo = settingStore(useShallow((state) => state.createSettingFlareInfo));
  const deleteSettingFlareInfo = settingStore(useShallow((state) => state.deleteSettingFlareInfo));
  const updateSettingFlareInfo = settingStore(useShallow((state) => state.updateSettingFlareInfo));
  const clearErrorSettingFlareInfo = settingStore(useShallow((state) => state.clearErrorSettingFlareInfo));
  const errorManageFlareInfo = settingStore(useShallow((state) => state.errorManageFlareInfo));
  const calculateReportAfterUpdateMasterFalreInfo = reportFlareStore(
    useShallow((state) => state.calculateReportAfterUpdateMasterFalreInfo),
  );

  const onClickEdit = (record: ResponseSettingFlareInfo) => {
    setIsEdit(record);
    setVisibleModal(true);
    formSettingFlareInfo.setFieldsValue({
      ...record,
    });
  };
  const deleteSetting = async (id: string) => {
    const confirm = await VocsModal.delete({
      message: "",
    });

    if (!confirm) return;
    deleteSettingFlareInfo(id).then(() => onFinishAction());
  };

  const {
    onChange,
    filter,
    page,
    setPage,
    loading,
    limit: pageSize,
    setLimit: setPageSize,
    data: sourceData,
  } = usePagination<ResponseSettingFlareInfo, FilterSettingFlareInfo>({
    action: getSettingFlareInfo,
    remoteData: dataSettingFlareInfo.data,
  });

  const getMasterLabel = (type: string, text: string) => {
    if (!text) return "";

    if (type === "emission" && emissionData[0]) {
      const optValue = emissionData[0].options.find((item) => item.value === text);
      return (optValue as Option).label;
    } else if (type === "unit" && flareInfoUnitOptions) {
      const optValue = flareInfoUnitOptions.find((item) => item.value === text);
      // console.log("OPT VAL : ", optValue, text);
      return (optValue as Option).label;
    } else if (type === "company" && companies?.length) {
      const findCompany = companies.find((item) => item._id === text);
      return findCompany?.companyName;
    } else return text;
  };

  const flareNameFilter = useMemo(() => {
    return filterFlareInfo.flareName.map((item) => {
      return {
        text: item,
        value: item,
      };
    });
  }, [filterFlareInfo.flareName]);
  const flareIdFilter = useMemo(() => {
    return filterFlareInfo.flareId.map((item) => {
      return {
        text: item,
        value: item,
      };
    });
  }, [filterFlareInfo.flareId]);
  const locationFilter = useMemo(() => {
    return companies.map((item) => {
      return {
        text: item.companyName,
        value: item._id,
      };
    });
  }, [dataSettingFlareInfo.data]);
  const flareTypeFilter = useMemo(() => {
    return flareTypeOptions.map((item) => {
      return {
        text: item.label,
        value: item.value,
      };
    });
  }, [dataSettingFlareInfo.data]);

  const handleTableChange: TableProps<ResponseSettingFlareInfo>["onChange"] = (_, filters) => {
    onChange(filters);
  };

  const getIndex = (index: number) => (page - 1) * pageSize + index;
  const columns: TableProps<ResponseSettingFlareInfo>["columns"] = [
    {
      title: "No",
      key: "no",
      render: (_, __, index) => getIndex(index + 1),
      align: "center",
    },
    {
      title: "Flare Name",
      dataIndex: "flareName",
      key: "flareName",
      render: (text) => <Text>{text}</Text>,
      filters: flareNameFilter,
      align: "left",
    },
    {
      title: "Flare ID",
      dataIndex: "flareId",
      key: "flareId",
      render: (text) => <Text>{text}</Text>,
      filters: flareIdFilter,
      align: "left",
    },
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      render: (text) => <Text>{getMasterLabel("company", text)}</Text>,
      filters: locationFilter,
      align: "left",
    },
    {
      title: "Flare Type",
      dataIndex: "flareType",
      key: "flareType",
      render: (text) => <Text>{text}</Text>,
      filters: flareTypeFilter,
      align: "left",
    },
    {
      title: "Emission",
      dataIndex: "emission",
      key: "emission",
      render: (text) => <Text>{getMasterLabel("emission", text)}</Text>,
      align: "left",
    },
    {
      title: "Unit",
      dataIndex: "unit",
      key: "unit",
      render: (text) => <Text>{getMasterLabel("unit", text)}</Text>,
      align: "left",
    },
    {
      title: "Emission Factor",
      dataIndex: "emissionFactor",
      key: "emissionFactor",
      render: (text) => <Text>{text}</Text>,
      align: "left",
    },
    {
      title: "Remark",
      dataIndex: "remark",
      key: "remark",
      render: (text) => <Text>{text}</Text>,
      align: "left",
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Space size="middle" direction={"horizontal"}>
          <Button
            className="border-transparent bg-transparent"
            size={"small"}
            onClick={() => onClickEdit(record)}
            disabled={!canEditTable(permissions)}
          >
            <BsPencilSquare size={18} />
          </Button>
          <Button
            className="border-transparent bg-transparent"
            size={"small"}
            onClick={() => deleteSetting(record._id)}
            disabled={!canEditTable(permissions)}
          >
            <BsTrashFill size={18} />
          </Button>
        </Space>
      ),
      align: "center",
    },
  ];

  useDidMount(() => {
    getFilterColumn("flareName");
    getFilterColumn("flareId");
    getSettingFlareInfo({ ...filter, page, limit: pageSize });
  });

  const onChangePage = (val: number) => {
    setPage(val);
  };

  const paginationProps: PaginationProps = {
    size: "small",
    pageSize: pageSize,
    current: page,
    total: totalDocs || 0,
    defaultCurrent: 1,
    onChange: onChangePage,
    showSizeChanger: false,
  };

  const onAddFlareInfo = () => {
    setVisibleModal(true);
  };

  const closeModal = () => {
    setVisibleModal(false);
    formSettingFlareInfo.resetFields();
    clearErrorSettingFlareInfo();
    setIsEdit(null);
  };

  const submitSettingFlareInfo = async () => {
    let checkError = false;
    await formSettingFlareInfo.validateFields().catch(() => {
      checkError = true;
    });
    if (checkError) return;

    const formData = formSettingFlareInfo.getFieldsValue();
    // console.log("form data ; ", formData);
    const formatValues = replaceUndefinedWithNull(formData);
    const finalData = trimAllKey(formatValues, ["flareName", "flareId"]) as CreateSettingFlareInfo;
    if (isEdit) {
      updateSettingFlareInfo({ ...finalData, _id: isEdit._id }).then(onFinishAction);
    } else createSettingFlareInfo(finalData).then(onFinishAction);
  };

  const onFinishAction = (errorMessage?: unknown) => {
    // console.log("errorMessage : ", errorMessage);
    if (errorMessage) {
      formSettingFlareInfo.setFields([
        {
          name: "flareName",
          errors: [""],
        },
      ]);
      return;
    } else {
      if (isEdit?._id)
        calculateReportAfterUpdateMasterFalreInfo({
          key: "flareName",
          _id: isEdit?._id,
        });
      messageApi.open({
        type: "success",
        content: CommonSuccess.SAVED_SUCCESSFULL,
      });
      getSettingFlareInfo({ ...filter, page, limit: pageSize });
      getFilterColumn("flareName");
      getFilterColumn("flareId");
      closeModal();
    }
  };

  // console.log("filterFlareInfo : ", filterFlareInfo);

  return (
    <Row className="w-full" id={"wrap-combustion-config-table"}>
      {contextHolder}
      {canEditTable(permissions) && (
        <Row className="w-full" justify={"end"}>
          <Button
            type="primary"
            size={"middle"}
            className="flex items-center"
            onClick={onAddFlareInfo}
            disabled={!canEditTable(permissions)}
          >
            <PlusCircleOutlined />
            New
          </Button>
        </Row>
      )}
      <Table
        columns={columns}
        dataSource={sourceData as ResponseSettingFlareInfo[]}
        className="w-full"
        loading={loading}
        pagination={false}
        onChange={handleTableChange}
      />

      <Row className="w-full mt-4" justify={"end"}>
        <Pagination {...paginationProps} />
      </Row>

      <Modal
        title={<ModalTitle title="Flare Information" />}
        open={visibleModal}
        width={1000}
        onCancel={closeModal}
        footer={
          <Row className="w-full border-t" justify={"end"}>
            <Button className="mt-4" key="submit" type="primary" loading={loadingManageFlareInfo} onClick={submitSettingFlareInfo}>
              Save
            </Button>
          </Row>
        }
        maskClosable={false}
        centered
      >
        {errorManageFlareInfo && (
          <Row className="w-full" justify={"center"}>
            <Text type="danger">{errorManageFlareInfo}</Text>
          </Row>
        )}
        <Form
          layout={"vertical"}
          name={"setting-flare-info"}
          autoComplete="off"
          form={formSettingFlareInfo}
          requiredMark={false}
          onFieldsChange={(_) => {
            if (errorManageFlareInfo) {
              clearErrorSettingFlareInfo();
              formSettingFlareInfo.setFields([
                {
                  name: "flareName",
                  errors: undefined,
                },
              ]);
            }
          }}
        >
          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12} lg={12}>
              <Form.Item
                label={<FormLabel label={"Flare Name"} required />}
                name="flareName"
                rules={[
                  {
                    required: true,
                    message: generateErrorMessage("Flare Name"),
                  },
                ]}
              >
                <Input maxLength={50} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={12}>
              <Form.Item
                label={<FormLabel label={"Flare ID"} required />}
                name="flareId"
                rules={[
                  {
                    required: true,
                    message: generateErrorMessage("Flare ID"),
                  },
                ]}
              >
                <Input maxLength={50} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={12}>
              <Form.Item
                label={<FormLabel label={"Location"} required />}
                name="location"
                rules={[
                  {
                    required: true,
                    message: generateErrorMessage("Location"),
                  },
                ]}
              >
                <Select>
                  {companies.map((item, index) => (
                    <Select.Option key={"location" + index} value={item._id}>
                      {item.companyName}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={12}>
              <Form.Item
                label={<FormLabel label={"Flare Type"} required />}
                name="flareType"
                rules={[
                  {
                    required: true,
                    message: generateErrorMessage("Flare Type"),
                  },
                ]}
              >
                <Select>
                  {flareTypeOptions.map((item, index) => (
                    <Select.Option key={"flareType" + index} value={item.value}>
                      {item.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={12}>
              <Form.Item label="Emission" name="emission" initialValue={"tvoc"}>
                <Select allowClear disabled>
                  {emissionData.length > 0 &&
                    emissionData[0].options.map((item, index) => (
                      <Select.Option key={"emission" + index} value={item.value}>
                        {item.label}
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={12}>
              <Form.Item label="Unit" name="unit" initialValue={"kg/kcal"}>
                <Select allowClear disabled>
                  {flareInfoUnitOptions.map((item, index) => (
                    <Select.Option key={"unit" + index} value={item.value}>
                      {item.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} lg={12}>
              <Form.Item
                label={<FormLabel label={"Emission Factor"} required />}
                name="emissionFactor"
                rules={[
                  {
                    required: true,
                    message: generateErrorMessage("Emission Factor"),
                  },
                ]}
              >
                <CustomInputNumber
                  onChange={(e) => formSettingFlareInfo.setFieldValue("emissionFactor", !isNil(e) ? parseFloat(`${e}`).toFixed(10) : null)}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={12}>
              <Form.Item label="Remark" name="remark">
                <Input />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </Row>
  );
};

export default FlareInformationTable;
